import type { InventoryStatus } from 'used-vehicle-contracts';
import type { StatusTone } from '../../components/ui/StatusText';

/**
 * How a stock vehicle is *shown* (plan §2.6).
 *
 * Only one inventory status is coloured. A car in the workshop is money standing still and somebody has
 * to finish it, so `IN_SERVICE` is `warning`; a car in the warehouse is where a car is supposed to be,
 * and a sold one is done. Colouring all three would mean colouring none of them.
 */
export const INVENTORY_STATUS_TONES: Record<InventoryStatus, StatusTone> = {
  IN_WAREHOUSE: 'muted',
  IN_SERVICE: 'warning',
  SOLD: 'muted',
};

export const inventoryStatusTone = (status: InventoryStatus): StatusTone => INVENTORY_STATUS_TONES[status] ?? 'muted';

export const INVENTORY_STATUSES: readonly InventoryStatus[] = ['IN_WAREHOUSE', 'IN_SERVICE', 'SOLD'];

/** The dossier's tabs, in the prototype's order. Documents arrives in phase 5. */
/**
 * The dossier's tabs. `VALUATION` is deliberately absent (business review 2026-09-23, plan §11.19): the
 * tab is hidden, not deleted — `VehicleValuationTab` still exists and comes back by adding the key here
 * and its branch in `VehicleDetailPage`. An old `?tab=VALUATION` link falls back to the overview.
 */
export const VEHICLE_TABS = ['OVERVIEW', 'INSPECTION', 'COSTS', 'DOCUMENTS', 'ACTIVITY'] as const;

export type VehicleTab = (typeof VEHICLE_TABS)[number];

export const isVehicleTab = (value: string | null): value is VehicleTab => (VEHICLE_TABS as readonly string[]).includes(value ?? '');

/** "Toyota Corolla" from whatever parts are recorded, without stray separators. */
export const vehicleTitle = (parts: { make?: string | null; model?: string | null }): string =>
  [parts.make, parts.model].filter((part): part is string => typeof part === 'string' && part.trim() !== '').join(' ');

/** The factory-equipment checkboxes of the prototype's Overview tab. */
export const EQUIPMENT_OPTIONS = [
  'Sunroof',
  'Leather seats',
  'Heated seats',
  'Navigation',
  'Reversing camera',
  'Parking sensors',
  'Alloy wheels',
  'Cruise control',
  'Keyless entry',
  'Bluetooth',
] as const;

/** The cost lines the seeded `costs` equation adds up, in the order the policy lists them. */
export const COST_LINE_IDS = [
  'licensing',
  'general',
  'registration',
  'transfer',
  'insurance',
  'commission',
  'fuel',
  'overhead',
  'holding',
  'repair',
  'taxTraffic',
  'otherDirect',
] as const;

export interface TotalCostLine {
  /** `purchasePrice`, a cost equation id, or `other` for whatever a customised policy adds beyond them. */
  id: string;
  amount: number;
}

const round3 = (value: number): number => Math.round(value * 1000) / 1000;

/**
 * "How is the projected total cost calculated?" (business review 2026-09-23, F2; plan §11.19).
 *
 * The purchase price plus every projected cost line with an amount, **summing to the figure on the
 * card**: a policy an administrator has changed may add something the seeded lines do not name, and
 * that difference is shown as its own `other` line rather than left unexplained.
 */
export const totalCostLines = (outputs: Readonly<Record<string, number | null>>, inputs: Readonly<Record<string, number>>): TotalCostLine[] => {
  const total = outputs.total;

  if (total === null || total === undefined) {
    return [];
  }

  const lines: TotalCostLine[] = [{ id: 'purchasePrice', amount: inputs.acquisitionBasis ?? 0 }];

  for (const id of COST_LINE_IDS) {
    const amount = outputs[id];

    if (amount !== null && amount !== undefined && amount !== 0) {
      lines.push({ id, amount });
    }
  }

  const remainder = round3(total - lines.reduce((sum, line) => sum + line.amount, 0));

  return remainder === 0 ? lines : [...lines, { id: 'other', amount: remainder }];
};
